import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Button, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import KPITile from "../analytics/kpi-tile";
import KpiBarChart from "./kpi-bar-chart";
import KpiListTable from "./kpi-list-table";
import KpiSection from "./kpi-section";
import CampaignStartSection from "./campaign-start-section";
import { KPI_COLORS } from "./fulfillment-kpi.styles";
import {
  LOAD_STATE,
  isLoaded,
  formatMonthLabel,
  formatPercent,
  lastCompleteRow,
  toBarSeries
} from "./fulfillment-kpi.helpers";
import { hasAccess } from "../../services/access";
import { ACCESS_CONFIG } from "../strings";
import { formatBusiness } from "../../services/business-time";
import { showError } from "../../services/toast-service";
import {
  getFulfillmentKpiOverview,
  getFulfillmentKpiBelowThreshold,
  getFulfillmentKpiCampaignStart,
  getFulfillmentKpiWithoutStart
} from "../../services/api-declaration";

// Same route as the other customer links in the app (built inline there too)
const customerLink = (customerId) =>
  `/dashboard/customers/${customerId}/overview`;

// First column of both customer lists
const customerCell = (row) => (
  <Link to={customerLink(row.customerId)}>
    {row.companyName || row.customerId}
  </Link>
);

// HubSpot is A&M-wide: other workspaces get no campaign start section
const showsCampaignStart = (campaign) => campaign?.reason !== "workspace";

// An answer of the wrong shape counts as a failed load
const acceptOverview = (data) =>
  data?.thresholds && Array.isArray(data.kpi1) && Array.isArray(data.kpi3)
    ? data
    : null;
const acceptList = (data) => (Array.isArray(data) ? data : null);
// "HubSpot nicht verbunden" is an answer, not a failed request
const acceptCampaign = (data) => (data?.thresholds ? data : null);

// One part of the page: loads on its own and shows as soon as its answer is
// there. Returns the data (or a LOAD_STATE) and a retry for this part only.
// Leaving the page before the answer came: no state update, no toast.
const usePart = (allowed, request, accept, onError) => {
  const [value, setValue] = useState(LOAD_STATE.LOADING);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    setValue(LOAD_STATE.LOADING);
    request().then(
      (response) => {
        if (!cancelled) {
          setValue(accept(response?.data?.data) ?? LOAD_STATE.FAILED);
        }
      },
      (error) => {
        if (cancelled) return;
        setValue(LOAD_STATE.FAILED);
        onError(error);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [allowed, request, accept, onError, attempt]);
  const retry = useCallback(() => setAttempt((count) => count + 1), []);
  return [value, retry];
};

// Fulfillment KPI dashboard (Asana 1219612776200513): campaign start within
// 30 days (HubSpot), first lead within 30 days and customers with >= 10 /
// >= 12 leads per month, plus the lists.
const FulfillmentKpis = () => {
  const { t, i18n } = useTranslation();
  const language = i18n.language?.startsWith("en") ? "en" : "de";
  const allowed = hasAccess(ACCESS_CONFIG.ADMIN_TIER);
  // one toast when several parts fail at once (e.g. the API is down)
  const lastToast = useRef(0);
  const notifyError = useCallback(
    (error) => {
      if (Date.now() - lastToast.current < 2000) return;
      lastToast.current = Date.now();
      showError(
        error?.response?.data?.message || t("fulfillmentKpis.loadError")
      );
    },
    [t]
  );
  const [overview, retryOverview] = usePart(
    allowed,
    getFulfillmentKpiOverview,
    acceptOverview,
    notifyError
  );
  const [withoutStart, retryWithoutStart] = usePart(
    allowed,
    getFulfillmentKpiWithoutStart,
    acceptList,
    notifyError
  );
  const [belowThreshold, retryBelowThreshold] = usePart(
    allowed,
    getFulfillmentKpiBelowThreshold,
    acceptList,
    notifyError
  );
  const [campaign, retryCampaign] = usePart(
    allowed,
    getFulfillmentKpiCampaignStart,
    acceptCampaign,
    notifyError
  );

  if (!allowed) return <Navigate to="/dashboard/funnels" />;
  if (overview === LOAD_STATE.LOADING) {
    return (
      <div className="d-flex justify-content-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }
  if (overview === LOAD_STATE.FAILED) {
    // the HubSpot section brings its own thresholds and can still show
    return (
      <div className="p-4 h-100 overflow-auto">
        <p className="text-danger mb-2">{t("fulfillmentKpis.loadError")}</p>
        <Button
          variant="outline-primary"
          size="sm"
          className="mb-4"
          onClick={retryOverview}
        >
          {t("fulfillmentKpis.retry")}
        </Button>
        {isLoaded(campaign) && showsCampaignStart(campaign) && (
          <CampaignStartSection
            data={campaign}
            thresholds={campaign.thresholds}
            language={language}
            runningLabel={t("fulfillmentKpis.running")}
            onRetry={retryCampaign}
          />
        )}
      </div>
    );
  }

  const { thresholds, kpi1, kpi3 } = overview;
  const start = lastCompleteRow(kpi1, "share");
  const leads = lastCompleteRow(kpi3, "share10");
  const runningLabel = t("fulfillmentKpis.running");

  return (
    <div className="p-4 h-100 overflow-auto">
      <h4 className="mb-1">{t("fulfillmentKpis.title")}</h4>
      <p className="text-secondary mb-4">
        {t("fulfillmentKpis.intro", {
          months: thresholds.historyMonths,
          minutes: thresholds.cacheMinutes
        })}
      </p>

      <div className="d-flex flex-column gap-5">
        {showsCampaignStart(campaign) && (
          <CampaignStartSection
            data={campaign}
            thresholds={thresholds}
            language={language}
            runningLabel={runningLabel}
            onRetry={retryCampaign}
          />
        )}

        <KpiSection
          title={t("fulfillmentKpis.start.title", {
            days: thresholds.kpi1Days
          })}
          help={t("fulfillmentKpis.start.help", { days: thresholds.kpi1Days })}
          tiles={
            <KPITile
              data={formatPercent(start?.share, language)}
              title={t("fulfillmentKpis.start.tile", {
                days: thresholds.kpi1Days,
                month: start ? formatMonthLabel(start.month, language) : "–",
                median: start?.medianDays ?? "–"
              })}
              bulletColor={KPI_COLORS.main}
            />
          }
          chart={
            <KpiBarChart
              colors={[KPI_COLORS.main]}
              language={language}
              runningLabel={runningLabel}
              series={[
                {
                  name: t("fulfillmentKpis.start.series"),
                  data: toBarSeries(
                    kpi1,
                    "share",
                    KPI_COLORS.main,
                    KPI_COLORS.mainRunning
                  )
                }
              ]}
              tooltipFor={(index) =>
                t("fulfillmentKpis.start.tooltip", kpi1[index] || {})
              }
            />
          }
        >
          <KpiListTable
            title={t("fulfillmentKpis.withoutStart.title", {
              days: thresholds.listMinDays
            })}
            rows={withoutStart}
            onRetry={retryWithoutStart}
            rowKey={(row) => row.customerId}
            columns={[
              { key: "customer", label: t("fulfillmentKpis.customer") },
              {
                key: "createdAt",
                label: t("fulfillmentKpis.withoutStart.createdAt")
              },
              {
                key: "days",
                label: t("fulfillmentKpis.withoutStart.days"),
                center: true
              },
              {
                key: "funnels",
                label: t("fulfillmentKpis.withoutStart.funnels"),
                center: true
              }
            ]}
            cells={(row) => ({
              customer: customerCell(row),
              createdAt: formatBusiness(row.createdAt, "DD.MM.YYYY"),
              days: row.daysSinceCreated,
              funnels: t("fulfillmentKpis.withoutStart.funnelCount", {
                count: row.funnels,
                drafts: row.draftFunnels
              })
            })}
          />
        </KpiSection>

        <KpiSection
          title={t("fulfillmentKpis.leads.title", { min: thresholds.kpi3Min })}
          help={t("fulfillmentKpis.leads.help")}
          tiles={
            <>
              <KPITile
                data={formatPercent(leads?.share10, language)}
                title={t("fulfillmentKpis.leads.tile", {
                  min: thresholds.kpi3Min,
                  month: leads ? formatMonthLabel(leads.month, language) : "–",
                  customers: leads?.customers ?? "–"
                })}
                bulletColor={KPI_COLORS.main}
              />
              <div className="mt-3">
                <KPITile
                  data={formatPercent(leads?.share12, language)}
                  title={t("fulfillmentKpis.leads.tileTarget", {
                    min: thresholds.kpi3Target
                  })}
                  bulletColor={KPI_COLORS.target}
                />
              </div>
            </>
          }
          chart={
            <KpiBarChart
              colors={[KPI_COLORS.main, KPI_COLORS.target]}
              language={language}
              runningLabel={runningLabel}
              series={[
                {
                  name: t("fulfillmentKpis.leads.series", {
                    min: thresholds.kpi3Min
                  }),
                  data: toBarSeries(
                    kpi3,
                    "share10",
                    KPI_COLORS.main,
                    KPI_COLORS.mainRunning
                  )
                },
                {
                  name: t("fulfillmentKpis.leads.series", {
                    min: thresholds.kpi3Target
                  }),
                  data: toBarSeries(
                    kpi3,
                    "share12",
                    KPI_COLORS.target,
                    KPI_COLORS.targetRunning
                  )
                }
              ]}
              tooltipFor={(index) =>
                t("fulfillmentKpis.leads.tooltip", kpi3[index] || {})
              }
            />
          }
        >
          <KpiListTable
            title={t("fulfillmentKpis.below.title", {
              min: thresholds.kpi3Min
            })}
            rows={belowThreshold}
            onRetry={retryBelowThreshold}
            rowKey={(row) => row.customerId}
            columns={[
              { key: "customer", label: t("fulfillmentKpis.customer") },
              {
                key: "running",
                label: t("fulfillmentKpis.below.running"),
                center: true
              },
              {
                key: "previous",
                label: t("fulfillmentKpis.below.previous"),
                center: true
              }
            ]}
            cells={(row) => ({
              customer: customerCell(row),
              running: row.runningMonth,
              previous: row.previousMonth
            })}
          />
        </KpiSection>
      </div>
    </div>
  );
};

export default FulfillmentKpis;
